import { useState } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { EventCombobox, type EventOption } from "@/components/ui/event-combobox";
import { useEvents } from "@/events/hooks/useEvents";
import { toastError } from "@/lib/toast-utils";
import { useLocation } from "wouter";

export default function PhotoSearch() {
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [bibNumber, setBibNumber] = useState<string>("");
  const [, setLocation] = useLocation();

  // Buscar eventos disponíveis
  const { data: eventsResponse, isLoading: eventsLoading } = useEvents({ page: 1, limit: 50, status: 'Approved' });
  const events = eventsResponse?.events || [];

  // Converter eventos para o formato do EventCombobox
  const eventOptions: EventOption[] = events.map((event) => ({
    id: event.id,
    title: event.title,
    date: event.date,
    location: event.location,
    status: event.status,
  }));

  const handleSearch = () => {
    // Construir parâmetros de busca
    const params = new URLSearchParams();
    
    if (bibNumber.trim()) {
      params.append('bib', bibNumber.trim());
    }
    
    if (selectedEventId && selectedEventId !== 'all') {
      params.append('eventId', selectedEventId);
    }

    // Se não tiver nenhum critério, mostrar erro
    if (!bibNumber.trim() && !selectedEventId) {
      toastError({
        title: "Critérios insuficientes",
        description: "Por favor, informe o número do peito ou selecione um evento.",
      });
      return;
    }

    // Redirecionar para a página de busca avançada
    setLocation(`/search?${params.toString()}`);
  };

  return (
    <div className="bg-white py-8 sm:py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Encontre Suas Fotos</h2>
            <p className="text-sm sm:text-base text-muted-foreground px-4">
              Digite seu número do peito e selecione o evento para encontrar suas fotos
            </p>
          </div>

          {/* Formulário de Busca */}
          <Card className="mb-8">
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Evento (Opcional)</label>
                  <EventCombobox
                    value={selectedEventId}
                    onValueChange={setSelectedEventId}
                    options={eventOptions}
                    isLoading={eventsLoading}
                    placeholder="Selecione um evento"
                    searchPlaceholder="Buscar evento..."
                    emptyText="Nenhum evento encontrado"
                    showAllOption={true}
                    allOptionLabel="Todos os eventos"
                    allowClear={true}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Número do Peito ou Nome</label>
                  <Input
                    type="text"
                    placeholder="Ex: 1234 ou João Silva"
                    value={bibNumber}
                    onChange={(e) => setBibNumber(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>

                <Button
                  onClick={handleSearch}
                  className="bg-primary hover:bg-teal-600 w-full sm:w-auto"
                >
                  <Search className="mr-2 h-4 w-4" />
                  Buscar Fotos
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}